import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, IconButton, Button, Box, Paper, Grid
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelIcon from '@mui/icons-material/Label';

import { handleExpedicaoAction, getExpedicaoDetails } from '../services/api';
import ConfirmModalMUI from '../warehouse/modals/ConfirmModalMUI';
import FeedbackModalMUI from '../warehouse/modals/FeedbackModalMUI';
import axios from 'axios';

const OrderDetails = ({ open, order, onClose, refreshExpedicoes }) => {
  const [details, setDetails] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const MEDIA_BASE_URL = 'https://plasticemployers.duckdns.org/media/';

  // Carrega detalhes sempre que a modal abrir/fechar
  useEffect(() => {
    if (open && order?.order_id) {
      getExpedicaoDetails(order.order_id)
      .then(response => {
        // 1) Loga tudo que vem do backend
        console.log("🏷️  getExpedicaoDetails response:", response);
        // 2) Se quiser só o body/data:
        console.log("📦  data:", response.data);
        setDetails(response.data);
      })
      .catch(error => {
        console.error("Erro ao buscar detalhes da expedição:", error);
      });
    } else {
      setDetails(null);
      setPdfUrl(old => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    }
  }, [open, order]);

  // Ações de confirmação
  const handleActionClick = action => {
    setSelectedAction(action);
    setConfirmModalOpen(true);
  };

  const handleConfirmAction = async () => {
    setConfirmModalOpen(false);
    setFeedbackModalOpen(true);
    setIsSubmitting(true);
    try {
      const { data } = await handleExpedicaoAction(order.order_id, selectedAction);
      setFeedbackMessage(data.message || data.url || 'Ação concluída');
      refreshExpedicoes?.();

      // limpa URL antigo
      setPdfUrl(old => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });

      // refaz fetch de detalhes já com decoded_label
      const { data: fresh } = await getExpedicaoDetails(order.order_id);
      setDetails(fresh);
    } catch (err) {
      console.error(err);
      setFeedbackMessage('Erro ao executar ação');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderiza tabela de volumes
  const renderVolumesTable = () => {
    const vols = details?.status_info?.volumes || [];
    if (!vols.length) return null;
    return (
      <Box component={Paper} elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6">Volumes</Typography>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Peso&nbsp;(kg)</th>
              <th>Altura&nbsp;(cm)</th>
              <th>Comprimento&nbsp;(cm)</th>
              <th>Largura&nbsp;(cm)</th>
            </tr>
          </thead>
          <tbody>
            {vols.map(v => (
              <tr key={v.id}>
                <td>{v.shipment_type}</td>
                <td>{v.weight}</td>
                <td>{v.height}</td>
                <td>{v.length}</td>
                <td>{v.width}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  // Carrega PDF quando decoded_label estiver disponível
  useEffect(() => {
    if (!details?.decoded_label) return;
    const fetchPdf = async () => {
      try {
        const resp = await axios.get(
          `${MEDIA_BASE_URL}${details.decoded_label}`,
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(resp.data);
        setPdfUrl(url);
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
        setPdfUrl(null);
      }
    };
    fetchPdf();
    return () => {
      setPdfUrl(old => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    };
  }, [details?.decoded_label]);

  // Preview PDF ou botão de gerar etiqueta
  const renderPdfPreview = () => {
    if (!details?.decoded_label) {
      return (
        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            A etiqueta ainda não foi gerada. Clique no botão abaixo para criar as etiquetas.
          </Typography>
          <IconButton color="primary" onClick={() => handleActionClick('etiquetas')}>
            <LabelIcon />
          </IconButton>
        </Box>
      );
    }
    if (!pdfUrl) {
      return (
        <Box mt={2}>
          <Typography variant="body2">Carregando PDF…</Typography>
        </Box>
      );
    }
    return (
      <Box mt={2}>
        <Typography variant="subtitle1" gutterBottom>
          Visualização do PDF:
        </Typography>
        <iframe
          src={pdfUrl}
          title="PDF Preview"
          width="100%"
          height="500px"
          style={{ border: 'none' }}
        />
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {details ? `Detalhes da Ordem ${details.order_id}` : 'Carregando…'}
        </DialogTitle>
        <DialogContent dividers>
          {!details ? (
            <Typography>Carregando detalhes…</Typography>
          ) : (
            <>
              <Typography variant="body1" mb={1}>
                <strong>Status:</strong> {details.state}
              </Typography>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>Ações</Typography>
                <Box display="flex" gap={1}>
                  <IconButton color="primary" onClick={() => handleActionClick('etiquetas')}>
                    <LabelIcon />
                  </IconButton>
                  
                </Box>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <Box component={Paper} elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Detalhes da Morada</Typography>
                    {details.morada ? (
                      <>                        
                        <Typography><strong>Cliente:</strong> {details.morada.client.name}</Typography>
                        <Typography><strong>Definição:</strong> {details.morada.defenicao}</Typography>
                        <Typography><strong>Nome da Pessoa a Entregar:</strong> {details.morada.nome_pessoa_entregar}</Typography>
                        <Typography><strong>Endereço:</strong> {details.morada.address}</Typography>
                        <Typography><strong>Código Postal / Cidade:</strong> {details.morada.postal_code} | {details.morada.city}</Typography>
                        <Typography><strong>Informação Complementar:</strong> {details.morada.suplementinformation}</Typography>
                        <Typography><strong>País:</strong> {details.morada.country}</Typography>
                      </>
                    ) : (
                      <Typography>Nenhuma morada associada.</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>{renderVolumesTable()}</Grid>
              </Grid>
              {renderPdfPreview()}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmModalMUI
        open={confirmModalOpen}
        message={`Tem certeza que deseja ${selectedAction} esta expedição?`}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmModalOpen(false)}
      />
      <FeedbackModalMUI
        open={feedbackModalOpen}
        isSubmitting={isSubmitting}
        submissionComplete={!isSubmitting && feedbackMessage}
        error={feedbackMessage !== 'Ação concluída' ? feedbackMessage : null}
        onClose={() => {
          setFeedbackModalOpen(false);
          setFeedbackMessage('');
        }}
      />
    </>
  );
};

export default OrderDetails;
